// CustomeWidgets.tsx
import React, { useEffect, useState } from 'react';
import IndicesWidget from '../components/IndicesList';
import TopGainersWidget from '../components/TopGainers';
import TopLosersWidget from '../components/TopLosers';
import HighLowWidget from '../components/HighLow';
import {
  getTopGainerLosers,
  getIndicesList,
  get52WeeksHighLow,
  getCustomWidgets
} from '../../../services/stockDetails';

interface GainerLoserItem {
  code: number;
  name: string;
  logo: string;
  price: number;
  changePercent: number;
  volume: number;
  priceChange: number;
}

interface HighLowItem {
  code: number;
  name: string;
  logo: string;
  price: number;
  changePercent: number;
  volume: number;
  priceChange: number;
  fiftyTwoWeekHigh?: number;
  fiftyTwoWeekLow?: number;
}

interface CustomWidgetItem {
  label: string;
  value: 'top_gainers_losers' | 'high_low' | 'List_of_Indices' | '52week_high_low';
}

interface IndicesListItem {
  name: string;
  price: number;
  priceChange: number;
  changePercent: number;
}

interface FetchedData {
  topGainers?: GainerLoserItem[];
  topLosers?: GainerLoserItem[];
  topHigh?: HighLowItem[];
  topLow?: HighLowItem[];
  indicesList?: IndicesListItem[];
}

interface CustomWidgetsProps {
  exchange?: string | null;
}

export const CustomeWidgets: React.FC<CustomWidgetsProps> = ({ exchange }) => {
  const [widgetConfig, setWidgetConfig] = useState<CustomWidgetItem | null>(null);
  const [topGainers, setTopGainers] = useState<GainerLoserItem[]>([]);
  const [topLosers, setTopLosers] = useState<GainerLoserItem[]>([]);
  const [topHigh, setHigh] = useState<HighLowItem[]>([]);
  const [topLow, setLow] = useState<HighLowItem[]>([]);
  const [indicesList, setIndicesList] = useState<IndicesListItem[]>([]);

  useEffect(() => {
    getCustomWidgets()
      .then((widgetsResponse) => {
        const widgetConfigData = widgetsResponse.data?.data as CustomWidgetItem | null;
        setWidgetConfig(widgetConfigData);

        const promisesToResolve: Promise<any>[] = [];
        const fetchedData: FetchedData = {};

        if (widgetConfigData?.value === 'top_gainers_losers') {
          promisesToResolve.push(
            getTopGainerLosers(exchange || 'nse', 'gain', 5)
              .then((res) => (fetchedData.topGainers = res.data?.data))
          );
          promisesToResolve.push(
            getTopGainerLosers(exchange || 'nse', 'lose', 5)
              .then((res) => (fetchedData.topLosers = res.data?.data))
          );
        } else if (widgetConfigData?.value === '52week_high_low') {
          promisesToResolve.push(
            get52WeeksHighLow(exchange || 'nse', 'high', 5)
              .then((res) => (fetchedData.topHigh = res.data?.data))
          );
          promisesToResolve.push(
            get52WeeksHighLow(exchange || 'nse', 'low', 5)
              .then((res) => (fetchedData.topLow = res.data?.data))
          );
        } else if (widgetConfigData?.value === 'List_of_Indices') {
          promisesToResolve.push(
            getIndicesList().then((res) => (fetchedData.indicesList = res.data?.data || res.data))
          );
        }

        return Promise.all(promisesToResolve).then(() => fetchedData);
      })
      .then((fetchedData: FetchedData) => {
        setTopGainers(fetchedData.topGainers || []);
        setTopLosers(fetchedData.topLosers || []);
        setHigh(fetchedData.topHigh || []);
        setLow(fetchedData.topLow || []);
        setIndicesList(fetchedData.indicesList || []);
      })
      .catch((err) => {
        console.error("Fetch Error:", err);
      });

  }, [exchange]);

  return (
    <>
      {widgetConfig && (
        <>
          {widgetConfig.value === 'top_gainers_losers' && (
            <>
              <TopGainersWidget key="top-gainers" data={topGainers} />
              <TopLosersWidget key="top-losers" data={topLosers} />
            </>
          )}
          {(widgetConfig.value === 'high_low' || widgetConfig.value === '52week_high_low') && (
            <HighLowWidget key="high-low" highData={topHigh} lowData={topLow} />
          )}
          {widgetConfig.value === 'List_of_Indices' && (
            <IndicesWidget key="List_of_Indices" data={indicesList} />
          )}
        </>
      )}
    </>
  );
};

export default CustomeWidgets;
